/**
 * PageCitationLinks Component
 *
 * Displays clickable page number badges that link to source pages.
 * Groups multiple citations on the same page and shows hover previews.
 */

import { useState } from 'react';
import { FileText } from 'lucide-react';
import type { Citation } from '../../types';

interface PageCitationLinksProps {
  citations: Citation[];
  onPageClick: (pageNum: number, citation: Citation) => void;
}

interface PageGroup {
  pageNumber: number;
  citations: Citation[];
}

export function PageCitationLinks({ citations, onPageClick }: PageCitationLinksProps) {
  const [hoveredPage, setHoveredPage] = useState<number | null>(null);

  if (!citations || citations.length === 0) {
    return null;
  }

  // Group citations by page number
  const pageGroups: PageGroup[] = [];
  const pageMap = new Map<number, Citation[]>();

  citations.forEach((citation) => {
    const existing = pageMap.get(citation.page_number);
    if (existing) {
      existing.push(citation);
    } else {
      pageMap.set(citation.page_number, [citation]);
    }
  });

  // Convert map to sorted array
  Array.from(pageMap.entries())
    .sort(([a], [b]) => a - b)
    .forEach(([pageNumber, pageCitations]) => {
      pageGroups.push({ pageNumber, citations: pageCitations });
    });

  // Truncate excerpt for preview
  const truncateExcerpt = (text: string, maxLength: number = 100): string => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  return (
    <div className="flex items-center gap-1 flex-wrap">
      <FileText size={14} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
      {pageGroups.map((group) => (
        <div key={group.pageNumber} className="relative">
          <button
            onClick={() => onPageClick(group.pageNumber, group.citations[0])}
            onMouseEnter={() => setHoveredPage(group.pageNumber)}
            onMouseLeave={() => setHoveredPage(null)}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 rounded-full hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
          >
            <span>p. {group.pageNumber}</span>
            {group.citations.length > 1 && (
              <span className="bg-primary-200 text-primary-800 dark:text-primary-300 px-1 rounded-full text-[10px]">
                {group.citations.length}
              </span>
            )}
          </button>

          {/* Hover preview tooltip */}
          {hoveredPage === group.pageNumber && (
            <div className="absolute z-50 bottom-full left-0 mb-1 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
              <div className="space-y-2">
                {group.citations.slice(0, 2).map((citation, index) => (
                  <div key={citation.citation_id || index}>
                    {group.citations.length > 1 && (
                      <span className="text-gray-400 dark:text-gray-500 text-[10px]">
                        Citation {index + 1}:
                      </span>
                    )}
                    <p className="text-gray-200 italic">
                      "{truncateExcerpt(citation.extracted_text)}"
                    </p>
                  </div>
                ))}
                {group.citations.length > 2 && (
                  <p className="text-gray-400 dark:text-gray-500 text-[10px]">
                    +{group.citations.length - 2} more citation
                    {group.citations.length - 2 !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
              {/* Arrow */}
              <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-900" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
